import React from 'react';
import { Check, X, Shield, AlertTriangle, Award, Clock, Wrench, FileCheck } from 'lucide-react';
import { useCurrency } from '../context/CurrencyContext';

const ComparisonSection = () => {
  const { t } = useCurrency();

  const comparisons = [
    {
      feature: t('comparison.features.points.title'),
      wisedrive: t('comparison.features.points.wisedrive'),
      local: t('comparison.features.points.local'),
      wiIcon: Check,
      loIcon: AlertTriangle,
    },
    {
      feature: t('comparison.features.warranty.title'),
      wisedrive: t('comparison.features.warranty.wisedrive'),
      local: t('comparison.features.warranty.local'),
      wiIcon: Check,
      loIcon: X,
    },
    {
      feature: t('comparison.features.report.title'),
      wisedrive: t('comparison.features.report.wisedrive'),
      local: t('comparison.features.report.local'),
      wiIcon: Check,
      loIcon: X,
    },
    {
      feature: t('comparison.features.fraud.title'),
      wisedrive: t('comparison.features.fraud.wisedrive'),
      local: t('comparison.features.fraud.local'),
      wiIcon: Check,
      loIcon: X,
    },
    {
      feature: t('comparison.features.obd.title'),
      wisedrive: t('comparison.features.obd.wisedrive'),
      local: t('comparison.features.obd.local'),
      wiIcon: Check,
      loIcon: AlertTriangle,
    },
    {
      feature: t('comparison.features.price.title'),
      wisedrive: t('comparison.features.price.wisedrive'),
      local: t('comparison.features.price.local'),
      wiIcon: Check,
      loIcon: X,
    },
  ];

  const benefits = [
    { icon: Shield, title: t('comparison.benefits.0.title'), desc: t('comparison.benefits.0.desc') },
    { icon: Award, title: t('comparison.benefits.1.title'), desc: t('comparison.benefits.1.desc') },
    { icon: Wrench, title: t('comparison.benefits.2.title'), desc: t('comparison.benefits.2.desc') },
    { icon: Clock, title: t('comparison.benefits.3.title'), desc: t('comparison.benefits.3.desc') },
  ];

  return (
    <section className="py-14 bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 relative overflow-hidden">
      {/* Background Pattern */}
      <div className="absolute inset-0 opacity-5">
        <div className="absolute inset-0" style={{ backgroundImage: 'radial-gradient(circle at 2px 2px, white 1px, transparent 0)', backgroundSize: '32px 32px' }} />
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        {/* Header */}
        <div className="text-center mb-10">
          <p className="text-blue-400 font-semibold text-sm uppercase tracking-wider mb-2">{t('comparison.badge')}</p>
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-3" style={{ fontFamily: 'Plus Jakarta Sans, sans-serif' }}>
            {t('comparison.title')}
          </h2>
          <p className="text-slate-300 max-w-2xl mx-auto">
            {t('comparison.subtitle').split('{protect}')[0]}
            <span className="text-blue-400 font-semibold">{t('comparison.protect')}</span>
            {t('comparison.subtitle').split('{protect}')[1]}
          </p>
        </div>

        {/* Comparison Table */}
        <div className="bg-white/5 backdrop-blur-sm rounded-2xl border border-white/10 overflow-hidden mb-10">
          {/* Table Header */}
          <div className="grid grid-cols-3 bg-white/10 px-6 py-4">
            <div className="text-slate-400 font-medium text-sm">{t('comparison.table.feature')}</div>
            <div className="text-center">
              <span className="bg-blue-600 text-white px-4 py-1.5 rounded-full text-sm font-bold">{t('comparison.table.wisedrive')}</span>
            </div>
            <div className="text-center">
              <span className="bg-slate-600 text-white px-4 py-1.5 rounded-full text-sm font-bold">{t('comparison.table.local')}</span>
            </div>
          </div>

          {/* Table Rows */}
          {comparisons.map((item, index) => (
            <div
              key={index}
              className={`grid grid-cols-3 px-6 py-4 items-center ${index % 2 === 0 ? 'bg-white/5' : ''}`}
            >
              <div className="text-white font-medium text-sm">{item.feature}</div>
              <div className="flex items-center justify-center gap-2">
                <item.wiIcon className="h-5 w-5 text-green-400" />
                <span className="text-green-300 text-sm hidden md:inline">{item.wisedrive}</span>
              </div>
              <div className="flex items-center justify-center gap-2">
                <item.loIcon className={`h-5 w-5 ${item.loIcon === X ? 'text-red-400' : 'text-amber-400'}`} />
                <span className="text-slate-400 text-sm hidden md:inline">{item.local}</span>
              </div>
            </div>
          ))}
        </div>

        {/* Benefits Grid */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          {benefits.map((benefit, index) => (
            <div key={index} className="bg-white/10 backdrop-blur-sm rounded-xl p-5 border border-white/10 text-center hover:bg-white/15 transition-colors">
              <div className="h-12 w-12 bg-blue-600/30 rounded-xl flex items-center justify-center mx-auto mb-3">
                <benefit.icon className="h-6 w-6 text-blue-400" />
              </div>
              <h3 className="text-white font-semibold mb-1">{benefit.title}</h3>
              <p className="text-slate-400 text-xs">{benefit.desc}</p>
            </div>
          ))}
        </div>

        {/* CTA */}
        <div className="text-center mt-10">
          <p className="text-slate-300 mb-4">
            {t('comparison.cta').split('{bold}')[0]}
            <span className="text-white font-semibold">{t('comparison.ctaBold')}</span>
            {t('comparison.cta').split('{bold}')[1]}
          </p>
        </div>
      </div>
    </section>
  );
};

export default ComparisonSection;

